import config from "../config/config";
import { searchStationAPI } from "./searchStationService";
import { searchStationPoiAPI } from "./searchStationService";
import { searchPoiAPI } from "./searchPoiService";

const BFF_API_BASE_URL = config.api.bff.baseUrl;
const BFF_SUCCESS_CODE = config.api.bff.responseCode.success;
const BFF_INTERNAL_SERVER_ERROR = config.api.bff.responseCode.internalServerError;
const SUGGEST_MAX_RESULTS = config.search.suggestMaxResults;
const SUGGEST_TIMEOUT_MS = config.search.suggestTimeoutMs;
const SEARCH_MAX_RESULTS = config.search.searchMaxResults;

const ENDPOINT_SUGGEST = config.search.endPointName.suggest;
const ENDPOINT_SEARCH = config.search.endPointName.search;

// パラメータキー
const PARAM_QUERY = config.search.queryParam;
const PARAM_LAT = 'lat';
const PARAM_LON = 'lon';
const PARAM_TIMEOUT = 'timeout';

const HTTP_METHOD_GET = 'GET';
const ERROR_PREFIX_HTTP = 'HTTP ';
const ERROR_PREFIX_NETWORK = 'Network Error: ';
const STATION_PREFIX = 'ST';

// 共通エラーハンドリングクラス
class BFFAPIError extends Error {
  constructor(code, message, status = null) {
    super(message);
    this.name = 'BFFAPIError';
    this.code = code;
    this.status = status;
  }
}

/**
 * サジェスト
 * ・SearchModalで入力中（ワード入力してn秒後）
 */
export const executeSuggest = async (searchTerm, center) => {
  const results = await performRealtimeSearch(searchTerm, center);
  return { data: { items: results } };
};

/**
 * 検索
 * ・検索確定時（SearchModalで入力して確定後にEnter押下時）
 * ・この辺りで再検索ボタン押下時
 * ・カテゴリボタン押下時
 * ・/place/xxx URLアクセス時
 * ・URLクエリパラメータ ?q=xxx ?poi=xxx アクセス時
 */
export const executeSearch = async (searchTerm, center, options = {}) => {

  const {
    isStationSearch = false,
    isPoiSearch = false,
  } = options;

  // 駅検索の場合の処理
  if (isStationSearch) {
    const results = await searchStationAPI(center);
    return {
      data: { items: results }
    };
  }

  // poi検索の場合の処理
  if (isPoiSearch) {
    // ST開始の場合は駅の検索
    if (searchTerm.startsWith(STATION_PREFIX)) {
      const results = await searchStationPoiAPI(searchTerm);
      return {
        data: { items: results }
      };
    }

    // 通常のpoi検索
    const results = await searchPoiAPI(searchTerm);
    return {
      data: { items: results }
    };
  }

  const results = await performSearch(searchTerm, center);
  return {
    data: { items: results }
  };
};

/**
* サジェスト実行
*/
async function performRealtimeSearch(query, center) {

  try {
    const suggestData = await callBFFAPI(
      ENDPOINT_SUGGEST,
      {
        [PARAM_QUERY]: query,
        [PARAM_LAT]: center.lat,
        [PARAM_LON]: center.lng,
        [PARAM_TIMEOUT]: SUGGEST_TIMEOUT_MS
      });

    if (!suggestData?.items || suggestData.items.length === 0) {
      return [];
    }
    return suggestData.items.slice(0, SUGGEST_MAX_RESULTS);
  } catch (error) {
    return [];
  }
}

/**
 * 検索実行
 */
async function performSearch(query, center) {
  try {
    const normalizedQuery = query.trim();

    // BFF API呼び出し
    const data = await callBFFAPI(
      ENDPOINT_SEARCH,
      {
        [PARAM_QUERY]: normalizedQuery,
        [PARAM_LAT]: center.lat,
        [PARAM_LON]: center.lng,
      }
    );

    if (!data.items || data.items.length === 0) {
      return []; // 検索結果なし
    }
    return data.items.slice(0, SEARCH_MAX_RESULTS);

  } catch (error) {
    return [];
  }
}

// BFF API呼び出し関数
async function callBFFAPI(endpoint, params = {}) {
  try {
    const url = new URL(`${BFF_API_BASE_URL}/${endpoint}`);

    // パラメータを追加
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key].toString());
      }
    });

    const response = await fetch(url.toString(), {
      method: HTTP_METHOD_GET,
    });

    if (!response.ok) {
      throw new BFFAPIError(
        response.status,
        `${ERROR_PREFIX_HTTP}${response.status}: ${response.statusText}`,
        response.status
      );
    }

    const result = await response.json();

    // BFF APIのエラーレスポンスをチェック
    if (result.code !== BFF_SUCCESS_CODE) {
      throw new BFFAPIError(result.code, result.message);
    }
    return result.data || {};
  } catch (error) {
    if (error instanceof BFFAPIError) {
      throw error;
    }
    // ネットワークエラーなど
    throw new BFFAPIError(BFF_INTERNAL_SERVER_ERROR, `${ERROR_PREFIX_NETWORK}${error.message}`);
  }
}
